/**
 * Pagina "Licenza & piano" del tenant corrente.
 *
 * BE v1.8.0:
 *  - GET /api/license-plans              → 5 piani disponibili (FREE/STARTER/BASIC/PRO/ENTERPRISE)
 *  - GET /api/tenant-subscriptions       → sottoscrizioni del tenant (lifecycle 7 stati)
 *  - GET /api/tenants/{id}/active-licenses
 *  - GET /api/tenants/{id}/active-quotas
 *  - GET /api/license-histories          → audit (read-only)
 *
 * Per upgrade/downgrade chiamiamo gli endpoint custom solo se l'utente è SuperAdmin.
 * Per gli end-user non admin restiamo sulla notifica al commerciale (come prima).
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Progress,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconCircleCheck,
  IconCrown,
  IconHourglass,
  IconInfoCircle,
  IconMail,
  IconPlayerPause,
  IconShieldCheck,
  IconX,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import dayjs from 'dayjs';
import { useAuth } from '@/app/providers/AuthProvider';
import { fetchList, fetchOne } from '@/shared/api/jsonapi/client';
import { httpRequest } from '@/shared/api/client';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';
import { useActiveLicenses, useActiveQuotas } from '@/shared/hooks/useLicense';
import { notifyError, notifySuccess } from '@/shared/lib/notify';
import type { Tenant } from '@/shared/types/entities';
import type { LicensePlan, TenantSubscription } from '@/shared/types/api.gen';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/licensing')({ component: LicensingPage });

type LicensePlanRow = LicensePlan & Denormalized & { id: string };
type TenantSubscriptionRow = TenantSubscription & Denormalized & { id: string; plan?: { id: string } };

type SubStatus = 'TRIAL' | 'ACTIVE' | 'GRACE_PERIOD' | 'PAUSED' | 'EXPIRED' | 'SUSPENDED' | 'CANCELLED';

const STATUS_COLOR: Record<SubStatus, string> = {
  TRIAL: 'cyan',
  ACTIVE: 'green',
  GRACE_PERIOD: 'yellow',
  PAUSED: 'orange',
  EXPIRED: 'gray',
  SUSPENDED: 'red',
  CANCELLED: 'gray',
};

const STATUS_LABEL: Record<SubStatus, string> = {
  TRIAL: 'Trial',
  ACTIVE: 'Attiva',
  GRACE_PERIOD: 'Periodo di grazia',
  PAUSED: 'In pausa',
  EXPIRED: 'Scaduta',
  SUSPENDED: 'Sospesa',
  CANCELLED: 'Annullata',
};

function LicensingPage() {
  const qc = useQueryClient();
  const { decoded } = useAuth();
  const tenantId = decoded?.tenantId ?? '';
  const isSuperAdmin = useIsSuperAdmin();

  const tenantQ = useQuery({
    queryKey: ['licensing-tenant', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchOne<Tenant>('tenants', tenantId),
  });

  const plansQ = useQuery({
    queryKey: ['license-plans-all'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchList<LicensePlanRow>('license-plans', { sort: ['sortOrder'], page: { size: 50 } }),
  });

  const subsQ = useQuery({
    queryKey: ['tenant-subscriptions', tenantId],
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    queryFn: () => fetchList<TenantSubscriptionRow>('tenant-subscriptions', {
      filter: { 'tenant.id': { eq: tenantId } },
      include: ['plan'],
      sort: ['-createdAt'],
      page: { size: 10 },
    }),
  });

  const activeLicensesQ = useActiveLicenses();
  const quotasQ = useActiveQuotas();

  const plans = plansQ.data?.data ?? [];
  const subscriptions = subsQ.data?.data ?? [];
  // Prendiamo la sub "vivente": qualsiasi stato attivo o in transizione
  const currentSub = subscriptions.find(
    (s) => s.status && !['CANCELLED', 'EXPIRED'].includes(s.status),
  );
  const currentStatus = (currentSub?.status as SubStatus | undefined) ?? null;
  const currentPlanId = currentSub?.plan?.id ?? null;
  const currentPlan = plans.find((p) => p.id === currentPlanId) ?? plans.find((p) => (p.code ?? '').toUpperCase() === (tenantQ.data?.plan ?? '').toUpperCase());

  const lifecycleAction = useMutation({
    mutationFn: async (payload: { kind: 'cancel' | 'pause' | 'resume'; reason?: string }) => {
      const url =
        payload.kind === 'cancel' ? `/api/tenants/${tenantId}/cancel-subscription` :
        payload.kind === 'pause' ? `/api/tenants/${tenantId}/pause-subscription` :
        `/api/tenants/${tenantId}/resume-subscription`;
      return httpRequest(url, {
        method: 'POST',
        endpointType: 'plain',
        body: payload.reason ? { reason: payload.reason } : {},
      });
    },
    onSuccess: () => {
      notifySuccess('Sottoscrizione aggiornata');
      qc.invalidateQueries({ queryKey: ['tenant-subscriptions', tenantId] });
      qc.invalidateQueries({ queryKey: ['active-licenses', tenantId] });
    },
    onError: (err) => notifyError(err),
  });

  const upgradePlan = useMutation({
    mutationFn: async (planCode: string) =>
      httpRequest(`/api/tenants/${tenantId}/upgrade-plan`, {
        method: 'POST',
        endpointType: 'plain',
        body: { planCode },
      }),
    onSuccess: () => {
      notifySuccess('Piano aggiornato');
      qc.invalidateQueries({ queryKey: ['tenant-subscriptions', tenantId] });
      qc.invalidateQueries({ queryKey: ['active-licenses', tenantId] });
      qc.invalidateQueries({ queryKey: ['licensing-tenant', tenantId] });
    },
    onError: (err) => notifyError(err),
  });

  const requestChange = useMutation({
    mutationFn: ({ target, direction }: { target: string; direction: 'upgrade' | 'downgrade' }) =>
      httpRequest('/api/admin/send-communications', {
        method: 'POST',
        endpointType: 'plain',
        body: {
          audience: 'role:SuperAdmin',
          subject: `Richiesta ${direction}: ${tenantQ.data?.name ?? tenantId} → ${target}`,
          body: `<p>Il tenant <b>${tenantQ.data?.name ?? tenantId}</b> ha richiesto un <b>${direction}</b> al piano <b>${target}</b>.</p>`,
        },
      }),
    onSuccess: (_d, vars) =>
      notifications.show({
        color: vars.direction === 'upgrade' ? 'green' : 'orange',
        title: 'Richiesta inviata',
        message: 'Il commerciale ti contatterà a breve.',
      }),
    onError: (err) => notifyError(err),
  });

  const askLifecycleAction = (kind: 'cancel' | 'pause' | 'resume') => {
    const titles: Record<typeof kind, string> = { cancel: 'Annulla sottoscrizione', pause: 'Metti in pausa', resume: 'Riprendi' };
    modals.openConfirmModal({
      title: titles[kind],
      children: (
        <Text size="sm">
          {kind === 'cancel' && 'La sottoscrizione resta utilizzabile fino alla scadenza. Confermi l\'annullamento?'}
          {kind === 'pause' && 'Il tenant resterà accessibile ma non verranno consumati seats. Confermi la pausa?'}
          {kind === 'resume' && 'Riattiva la sottoscrizione in pausa. Confermi?'}
        </Text>
      ),
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      confirmProps: { color: kind === 'cancel' ? 'red' : 'orange' },
      onConfirm: () => lifecycleAction.mutate({ kind }),
    });
  };

  if (tenantQ.isLoading || plansQ.isLoading) {
    return (
      <Stack>
        <Skeleton h={60} radius="md" />
        <Skeleton h={180} radius="md" />
        <Skeleton h={160} radius="md" />
      </Stack>
    );
  }
  if (tenantQ.isError || plansQ.isError) {
    return <QueryErrorAlert error={tenantQ.error ?? plansQ.error} onRetry={() => { void tenantQ.refetch(); void plansQ.refetch(); }} />;
  }

  return (
    <Stack>
      <Title order={1}>
        <IconCrown size={28} style={{ verticalAlign: 'middle', marginRight: 8 }} />
        Licenza & Piano
      </Title>

      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" wrap="nowrap">
          <Group>
            <ThemeIcon size="xl" radius="md" variant="light" color={currentStatus ? STATUS_COLOR[currentStatus] : 'gray'}>
              <IconShieldCheck size={28} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Piano corrente</Text>
              <Group gap="xs">
                <Title order={3}>{currentPlan?.name ?? currentPlan?.code ?? tenantQ.data?.plan ?? 'free'}</Title>
                {currentStatus && (
                  <Badge color={STATUS_COLOR[currentStatus]}>{STATUS_LABEL[currentStatus]}</Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">Tenant: {tenantQ.data?.name ?? tenantId}</Text>
              {currentSub?.nextBillingDate && (
                <Text size="xs" c="dimmed">
                  Prossimo rinnovo: {dayjs(currentSub.nextBillingDate).format('DD/MM/YYYY')}
                </Text>
              )}
              {currentSub?.trialEndsAt && currentStatus === 'TRIAL' && (
                <Text size="xs" c="dimmed">
                  Trial fino al {dayjs(currentSub.trialEndsAt).format('DD/MM/YYYY')}
                </Text>
              )}
            </div>
          </Group>

          {isSuperAdmin && currentSub && (
            <Group gap="xs">
              {currentStatus === 'PAUSED' ? (
                <Button variant="light" color="green" onClick={() => askLifecycleAction('resume')} loading={lifecycleAction.isPending}>
                  Riprendi
                </Button>
              ) : (
                <Button variant="light" color="orange" leftSection={<IconPlayerPause size={14} />} onClick={() => askLifecycleAction('pause')} loading={lifecycleAction.isPending}>
                  Pausa
                </Button>
              )}
              {currentStatus && currentStatus !== 'CANCELLED' && (
                <Button variant="light" color="red" leftSection={<IconX size={14} />} onClick={() => askLifecycleAction('cancel')} loading={lifecycleAction.isPending}>
                  Annulla
                </Button>
              )}
            </Group>
          )}
        </Group>
      </Card>

      {/* QUOTE */}
      <Title order={3}>Utilizzo e quote</Title>
      <Card withBorder padding="lg" radius="md">
        {quotasQ.isLoading ? (
          <Center py="md"><Loader size="sm" /></Center>
        ) : Object.keys(quotasQ.data ?? {}).length === 0 ? (
          <Text size="sm" c="dimmed">Nessuna quota tracciata sul piano corrente.</Text>
        ) : (
          <Stack>
            {Object.entries(quotasQ.data ?? {}).map(([key, q]) => {
              const current = q.current ?? 0;
              const limit = q.limit ?? 0;
              const pct = limit > 0 ? Math.min(100, (current / limit) * 100) : 0;
              const over = limit > 0 && current >= limit;
              return (
                <div key={key}>
                  <Group justify="space-between" mb={4}>
                    <Text fw={500} size="sm">{key}</Text>
                    <Text size="xs" c={over ? 'red' : 'dimmed'}>
                      {current}{limit > 0 ? ` / ${limit}` : ''} {q.unit ?? ''}
                    </Text>
                  </Group>
                  {limit > 0 && (
                    <Progress value={pct} color={over ? 'red' : pct > 80 ? 'yellow' : 'green'} size="md" />
                  )}
                </div>
              );
            })}
          </Stack>
        )}
      </Card>

      {/* CONFRONTO PIANI */}
      <Group justify="space-between" align="flex-end">
        <Title order={3}>Piani disponibili</Title>
        <Text size="xs" c="dimmed">
          {isSuperAdmin ? 'Da SuperAdmin puoi cambiare piano direttamente.' : 'Richiedi la modifica al commerciale.'}
        </Text>
      </Group>

      <Card withBorder padding={0} radius="md">
        <ScrollArea type="auto">
          <SimpleGrid cols={{ base: 1, sm: 2, md: plans.length || 1 }} spacing={0}>
            {plans.map((p) => {
              const isCurrent = currentPlan?.id === p.id;
              return (
                <Stack key={p.id} p="md" gap="xs" style={{ borderRight: '1px solid var(--mantine-color-gray-3)' }}>
                  <Group gap={6}>
                    <Title order={5}>{p.name ?? p.code}</Title>
                    {isCurrent && <Badge color="green" size="xs">Attuale</Badge>}
                    {p.isTrialEligible && !isCurrent && <Badge color="cyan" size="xs" variant="light">Trial</Badge>}
                  </Group>
                  <Badge variant="light" size="sm">{p.tier ?? ''}</Badge>
                  {p.description && <Text size="xs" c="dimmed">{p.description}</Text>}
                  {p.maxSeats != null && <Text size="xs">Max seats: {p.maxSeats}</Text>}

                  {!isCurrent && isSuperAdmin && (
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconArrowUp size={12} />}
                      loading={upgradePlan.isPending}
                      onClick={() => upgradePlan.mutate(p.code ?? '')}
                      disabled={!p.code}
                    >
                      Passa a {p.name ?? p.code}
                    </Button>
                  )}
                  {!isCurrent && !isSuperAdmin && (
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconMail size={12} />}
                      loading={requestChange.isPending}
                      onClick={() => requestChange.mutate({ target: p.name ?? p.code ?? '', direction: 'upgrade' })}
                    >
                      Richiedi
                    </Button>
                  )}
                </Stack>
              );
            })}
          </SimpleGrid>
        </ScrollArea>
      </Card>

      {/* LICENZE ATTIVE */}
      <Title order={3}>Licenze attive</Title>
      <Card withBorder padding="md" radius="md">
        {activeLicensesQ.isLoading ? (
          <Center py="md"><Loader size="sm" /></Center>
        ) : (activeLicensesQ.data ?? []).length === 0 ? (
          <Alert color="gray" variant="light" icon={<IconInfoCircle size={14} />}>
            Nessuna licenza specifica attiva. Il tenant ha solo il piano base.
          </Alert>
        ) : (
          <Group gap="xs">
            {(activeLicensesQ.data ?? []).map((code) => (
              <Badge key={code} leftSection={<IconCircleCheck size={12} />} variant="light" color="green">
                {code}
              </Badge>
            ))}
          </Group>
        )}
      </Card>

      {/* STORICO SUBSCRIPTION */}
      <Title order={3}>Storico sottoscrizioni</Title>
      <Card withBorder padding={0} radius="md">
        {subsQ.isLoading ? (
          <Center py="md"><Loader size="sm" /></Center>
        ) : subscriptions.length === 0 ? (
          <Center py="lg">
            <Stack align="center" gap="xs">
              <ThemeIcon variant="light" color="gray" radius="xl" size="xl">
                <IconHourglass size={24} />
              </ThemeIcon>
              <Text size="sm" c="dimmed">Nessuna sottoscrizione registrata.</Text>
            </Stack>
          </Center>
        ) : (
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Inizio</Table.Th>
                <Table.Th>Fine</Table.Th>
                <Table.Th>Stato</Table.Th>
                <Table.Th>Seats</Table.Th>
                <Table.Th>Periodo</Table.Th>
                <Table.Th>Auto-renew</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {subscriptions.map((s) => {
                const st = (s.status as SubStatus) ?? 'CANCELLED';
                return (
                  <Table.Tr key={s.id}>
                    <Table.Td>{s.startDate ? dayjs(s.startDate).format('DD/MM/YYYY') : '—'}</Table.Td>
                    <Table.Td>{s.endDate ? dayjs(s.endDate).format('DD/MM/YYYY') : '—'}</Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLOR[st] ?? 'gray'} size="sm" variant="light">
                        {STATUS_LABEL[st] ?? st}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{s.seats ?? '—'}</Table.Td>
                    <Table.Td>{s.billingPeriod ?? '—'}</Table.Td>
                    <Table.Td>{s.autoRenew ? 'Sì' : 'No'}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Group justify="flex-end">
        <Button component={Link} to="/billing" variant="subtle" size="xs">
          Vai a fatturazione →
        </Button>
        {isSuperAdmin && currentPlan && (
          <Button
            component={Link}
            to="/admin/licenses-catalog"
            variant="subtle"
            size="xs"
            leftSection={<IconArrowDown size={12} />}
          >
            Catalogo licenze
          </Button>
        )}
      </Group>
    </Stack>
  );
}
